import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from 'Context';
import { Pathname } from 'Routes';
import { revokeCurrentDevice } from 'Utils/deviceSession';
import { settleDesignAfterLogout } from 'Utils/uiSwitchOverlay';

/**
 * useLogout — the core sign-out used by settings / account.
 *
 * Clears the auth token + verified flag, flips sign-in state, and returns to the
 * sign-in screen. (The header's own logout additionally flushes in-flight upload
 * drafts and redux caches; those are header-scoped and not needed for a plain
 * sign-out from settings.)
 */
export const useLogout = () => {
  const { removeToken, setUserSignInStatus, removeEmailVerified, setPreferredEmail } = useContext(AuthContext);
  const navigate = useNavigate();

  return () => {
    // Navigate FIRST. If the session is cleared while the router still sits on
    // a signed-in path, the signed-out route table gets a chance to paint that
    // path (this is what made /settings flash the privacy screen on logout).
    navigate(Pathname.authentication.newsignIn, { replace: true });
    // Best-effort: revoke this session server-side (must run before removeToken
    // clears the token). Fire-and-forget; never blocks sign-out.
    try { revokeCurrentDevice(); } catch (e) { /* noop */ }
    try { removeToken(); } catch (e) { /* noop */ }
    try { setUserSignInStatus(false); } catch (e) { /* noop */ }
    try { removeEmailVerified(); } catch (e) { /* noop */ }
    try { setPreferredEmail(''); } catch (e) { /* noop */ }
    // Sign-in screen shows the backend's default design, not this user's pick.
    settleDesignAfterLogout();
  };
};
